import { useState, useEffect, useMemo } from "react";
import {
  CircularProgress,
  Chip,
  Paper,
  Typography,
  Button,
  Box,
  Divider,
  Backdrop,
  Switch,
  Snackbar,
  IconButton,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import Categories from "../../components/categories/categories";
import { useDispatch, useSelector } from "react-redux";
import Menu from "../../pages/menu/menu";
import {
  getCategories,
  getPreference,
  setPreference,
  getCategoryPreferences,
  setCategoryPreferences,
} from "../../actions";
import "./user-settings.scss";
import isEmpty from "lodash/isEmpty";
import { isTokenUsable } from "../../utils/session";
import { sdlConsentAvailable } from "../../components/sdl-consent/sdl-audience";
import SdlStandingConsentSection from "../../components/sdl-consent/sdl-standing-consent-section";
import { glConsentAvailable } from "../../components/gl-consent/gl-consent-audience";
import GlConsentSection from "../../components/gl-consent/gl-consent-section";
import { isInstitutionalLoginEnabled } from "../../components/institutional/institutional-flags";
import AccountConnectionsSection from "../../components/account/AccountConnectionsSection";
import RecordingSettingSection from "../../components/replay-consent/recording-setting-section";
import { useHistory, Link as RouterLink } from "react-router-dom";
import { DELETE_ACCOUNT_PATH } from "../../utils/account-deletion-signout";
import "../delete-account/delete-account.scss";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    minHeight: "100vh",
    height: "auto",
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

// Signed out (a visitor from the daily question or sign-in, or the privacy
// policy's 2.4 link): only the session-recording switch, which is the
// browser's choice and needs no account, plus a way to sign in for the rest.
// Nothing here calls the API: a tokenless API call on this route would send
// the visitor to /login (store.js), and opting out must not need an account.
const SIGN_IN_FOR_SETTINGS = "/login?next=%2Fuser-settings";

function SignedOutSettingsPage() {
  const classes = useStyles();
  return (
    <Box className={`${classes.container} settings-container`}>
      <Box
        sx={{
          display: "flex",
          height: "max-content",
          width: "100%",
          justifyContent: "center",
          padding: "24px",
          flexDirection: "column",
        }}
      >
        <Typography variant="h4">Settings</Typography>
        <RecordingSettingSection />
        <Paper elevation={2} className="settings-paper-container settings-account-paper" component="section"
          aria-labelledby="settings-account-title">
          <Typography variant="h6" id="settings-account-title" gutterBottom>
            Account
          </Typography>
          <Box className="settings-account-row">
            <Typography variant="body2">
              Sign in to see your exam preferences, notifications and account.
            </Typography>
            <Button
              color="primary"
              component={RouterLink}
              data-testid="settings-sign-in"
              to={SIGN_IN_FOR_SETTINGS}
              variant="contained"
            >
              Sign in
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

// Signed in: a stored token that is usable (utils/session isTokenUsable: its
// claims decode to a numeric exp still in the future, the same expiry rule
// store.js applies before it clears the session and sends the visitor to "/"
// on the first API call). An expired or undecodable token gets the signed-out
// view instead, so the recording switch stays reachable and nothing records
// on the way (/login is a recording page). Known gap, left to the #722
// stale-session guard on purpose: a token the SERVER rejects before it expires
// still renders the full page, and the guard then sends the visitor to
// /login?next=/user-settings.
const hasSession = () => {
  try {
    return isTokenUsable(window.localStorage.getItem("token"));
  } catch (e) {
    return false;
  }
};

export default function UserSettingsPage() {
  return hasSession() ? <SignedInSettingsPage /> : <SignedOutSettingsPage />;
}

function SignedInSettingsPage() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const categoriesData = useSelector((state) => state.categoriesList);
  const categoryPreferences = useSelector(
    (state) => state.getCategoryPreferences?.getCategoryPreferences
  );
  const userPreferenceData = useSelector(
    (state) => state.getUserPreference?.preference
  );

  const {
    preference: setUserPreferences,
    loading: setUserPreferencesLoading,
    errorMsg: setUserPreferenceError,
  } = useSelector((state) => state.setUserPreference || {});
  const cPPostData = useSelector((state) => state.setCategoryPreferences);

  const [selectedExamType, setSelectedExamType] = useState([]);

  const [examTypeList, setExamTypeList] = useState([]);

  const [emailPracticeQs, setEmailPracticeQs] = useState(false);
  const [smsNotifications, setSmsNotifications] = useState(true);

  const [originalPreferences, setOriginalPreferences] = useState({
    examType: [],
    emailPracticeQs: emailPracticeQs,
    smsNotifications: true,
  });

  const [open, setOpen] = useState(false);
  const [updateSettingStatus, setUpdateSettingsStatus] = useState(false);
  const [isExamTypeEmpty, setIsExamTypeEmpty] = useState(false);
  const [smsConsentSnackbar, setSmsConsentSnackbar] = useState({ open: false, optIn: false });

  const setUserPreferencesOnFetch = (preferences) => {
    setEmailPracticeQs(preferences?.emailPracticeQuestions);
    setSmsNotifications(preferences?.smsNotifications ?? true);
    setOriginalPreferences((prev) => ({
      ...prev,
      emailPracticeQs: preferences?.emailPracticeQuestions,
      smsNotifications: preferences?.smsNotifications ?? true,
    }));
  };

  useEffect(() => {
    if (categoriesData && Object.keys(categoriesData)?.length) {
      setExamTypeList(categoriesData?.categoriesList?.examType || []);
    }
  }, [categoriesData]);

  useEffect(() => {
    if (userPreferenceData) {
      setUserPreferencesOnFetch(userPreferenceData);
    }
  }, [userPreferenceData]);

  useEffect(() => {
    if (setUserPreferences) {
      setUserPreferencesOnFetch(setUserPreferences);
    }
  }, [setUserPreferences]);

  useEffect(() => {
    if (categoryPreferences && Object.keys(categoryPreferences)?.length) {
      const examTypes = categoryPreferences?.examType || [];
      setSelectedExamType(examTypes);
      setIsExamTypeEmpty(examTypes.length === 0); 

      setOriginalPreferences((prev) => ({
        ...prev,
        examType: [...(categoryPreferences?.examType || [])],
      }));
    }
  }, [categoryPreferences]);

  useEffect(() => {
    if (
      !!Object.keys(cPPostData?.setCategoryPreferences || {}).length &&
      !cPPostData?.loading
    ) {
      const userId = window.localStorage.getItem("userId");
      if (userId) {
        dispatch(getCategoryPreferences(userId));
      }
    }
  }, [cPPostData]);

  const setSnackbarPostSettingUpdate = () => {
    // A failed save lands on SET_PREFERENCE_ERROR, whose slice is
    // { errorMsg, loading:false } with `preference` undefined (QUI#540 audit
    // finding X4). Without folding errorMsg in, the failed save reads as
    // "not called" and slips past the snackbar silently. Surface the error the
    // same way overall-performance reads state.getUserPreference.errorMsg.
    const userPrefCalled =
      !isEmpty(setUserPreferences) ||
      setUserPreferencesLoading === true ||
      Boolean(setUserPreferenceError);
    const catPrefCalled =
      !isEmpty(cPPostData?.setCategoryPreferences) ||
      cPPostData?.loading === true;

    const userPrefDone = !setUserPreferencesLoading;
    const catPrefDone = !cPPostData?.loading;

    const userPrefSuccess =
      !setUserPreferenceError &&
      setUserPreferences?.emailPracticeQuestions !== undefined;
    const catPrefSuccess = cPPostData?.setCategoryPreferences !== null;

    const allDone =
      (userPrefCalled ? userPrefDone : true) &&
      (catPrefCalled ? catPrefDone : true);

    if (allDone) {
      const allSuccess =
        (userPrefCalled ? userPrefSuccess : true) &&
        (catPrefCalled ? catPrefSuccess : true);

      setUpdateSettingsStatus(allSuccess);
      setOpen(true);
    }
  };
  useEffect(() => {
    if (
      (!isCategoryPreferenceModified() && !isUserPreferenceModified()) ||
      (!Object.keys(cPPostData)?.length &&
      isEmpty(setUserPreferences) &&
      !setUserPreferenceError)
    )
      return false;
    setSnackbarPostSettingUpdate();
  }, [
    setUserPreferences,
    setUserPreferencesLoading,
    setUserPreferenceError,
    cPPostData,
  ]);

  useEffect(() => {
    if (
      categoriesData &&
      !Object.keys(categoriesData)?.length &&
      categoriesData.loading === undefined
    ) {
      dispatch(getCategories());
    }

    const userId = window.localStorage.getItem("userId");
    if (userId) {
      if (!categoryPreferences) {
        dispatch(getCategoryPreferences(userId));
      }
      if (!userPreferenceData) {
        dispatch(getPreference(userId));
      }
    }
  }, [dispatch]);

  const getLabelById = (id, list) => {
    const match = list.find((item) => item._id === id || item.id === id);
    return match?.name || id;
  };

  const handleDelete = (id, categoryType) => {
    if (categoryType === "examType") {
      const newExamTypes = [...selectedExamType].filter((item) => item !== id);
      setSelectedExamType(newExamTypes);
      setIsExamTypeEmpty(newExamTypes.length === 0);
    }
  };

  const toggleEmailQs = (evt) => {
    const newMode = evt.target.checked;
    setEmailPracticeQs(newMode);
  };

  const toggleSmsNotifications = (evt) => {
    const checked = evt.target.checked;
    setSmsNotifications(checked);
    setSmsConsentSnackbar({ open: true, optIn: checked });
  };
  const arraysEqual = (arr1, arr2) => {
    const a = [...arr1].sort();
    const b = [...arr2].sort();
    return a.length === b.length && a.every((val, index) => val === b[index]);
  };
  const isCategoryPreferenceModified = () => {
    return !arraysEqual(originalPreferences.examType, selectedExamType);
  };

  const isUserPreferenceModified = () => {
    return (
      originalPreferences.emailPracticeQs !== emailPracticeQs ||
      originalPreferences.smsNotifications !== smsNotifications
    );
  };

  const isSaveEnabled = useMemo(() => {
    return (isCategoryPreferenceModified() || isUserPreferenceModified()) &&
           selectedExamType.length > 0;
  }, [originalPreferences, selectedExamType, emailPracticeQs, smsNotifications]);


  const handleSaveSettings = () => {
    const userId = window.localStorage.getItem("userId");
    if (isCategoryPreferenceModified()) {
      const categoryMap = {
        discipline: [...(categoryPreferences?.discipline || [])],
        organSystem: [...(categoryPreferences?.organSystem || [])],
        examType: [...(selectedExamType || [])],
      };
      dispatch(setCategoryPreferences({ userId, categoryMap }));
    }

    if (isUserPreferenceModified()) {
      const settingsParam = {
        userId,
        body: {
          emailPracticeQuestions: emailPracticeQs,
          smsNotifications: smsNotifications,
        },
      };
      dispatch(setPreference(settingsParam));
    }
  };

  return (
    <>
      <Backdrop
        className={classes.backdrop}
        open={!!cPPostData?.loading || !!setUserPreferencesLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={3000}
        open={open}
        onClose={() => setOpen(false)}
      >
        <Box
          className={`${
            updateSettingStatus ? "success-alert" : "success-warn"
          } save-setting-snackbar`}
        >
          {updateSettingStatus
            ? `Settings saved successfully`
            : `Failed to save settings. Please try again`}
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => setOpen(false)}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Snackbar>
      <Snackbar
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      autoHideDuration={3000}
      open={isExamTypeEmpty}
      onClose={() => setIsExamTypeEmpty(false)}
    >
      <Box className="success-warn save-setting-snackbar">
        Please select at least one exam type
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={() => setIsExamTypeEmpty(false)}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={4000}
        open={smsConsentSnackbar.open}
        onClose={() => setSmsConsentSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Box className={`${smsConsentSnackbar.optIn ? "success-alert" : "success-warn"} save-setting-snackbar`}>
          {smsConsentSnackbar.optIn
            ? "You have opted in to receive SMS messages from QLearn by Medlearnity. Text STOP to opt out. Text HELP for support. Msg & data rates may apply."
            : "You have opted out of QLearn SMS messages."}
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => setSmsConsentSnackbar((prev) => ({ ...prev, open: false }))}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Snackbar>
      <Box className={`${classes.container} settings-container`}>
        <Menu />
        <Box
          sx={{
            display: "flex",
            height: "max-content",
            width: "100%",
            justifyContent: "center",
            padding: "24px",
            flexDirection: "column",
          }}
        >
          <Typography variant="h4">User Settings</Typography>
          {categoriesData?.loading ? (
            <CircularProgress color="inherit" />
          ) : (
            <Paper elevation={2} className="settings-paper-container">
              <Typography variant="h6" gutterBottom>
                Your Selected Preferences
              </Typography>
              <Typography
                variant="subtitle2"
                color="secondary"
                gutterBottom
                className="subtitleStyle"
              >
                Select your default preferences. These will appear in the left
                menu for quick access.
              </Typography>
              <Divider />
              <Box
                sx={{
                  padding: "10px 0px",
                }}
              >
                {examTypeList?.length !== 0 && selectedExamType?.length > 0 && (
                  <>
                    <Typography variant="body2">Exam Type</Typography>
                    <Box className="chipWrapper">
                      {selectedExamType.map((item) => (
                        <Chip
                          key={item}
                          label={getLabelById(item, examTypeList)}
                          onDelete={() => handleDelete(item, "examType")}
                          className="preferenceChips"
                        />
                      ))}
                    </Box>
                  </>
                )}
              </Box>

              <Divider />
              <Box sx={{ mb: 2, mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="secondary"
                  gutterBottom
                  className="subtitleStyle"
                >
                  Select your category preferences below
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Categories
                    onExamTypeCatSelect={(quesArr) => {
                      setSelectedExamType([...quesArr]);
                      setIsExamTypeEmpty(quesArr.length === 0);
                    }}
                    selectedExamType={selectedExamType}
                    examType={examTypeList}
                    isViewDisabled={false}
                    hideCategoryWhenEmpty={true}
                    hidePowerSearch={true}
                  />
                </Box>
                <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                  <Typography>Email Practice Question:</Typography>
                  <Switch
                    checked={emailPracticeQs}
                    onChange={(e) => toggleEmailQs(e)}
                    name="emailPracticeQuestion"
                    color="primary"
                  />
                </Box>
                <Box sx={{ mb: 1, display: "flex", alignItems: "center" }}>
                  <Typography>SMS Notifications:</Typography>
                  <Switch
                    checked={smsNotifications}
                    onChange={(e) => toggleSmsNotifications(e)}
                    name="smsNotifications"
                    color="primary"
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" className="subtitleStyle">
                    By opting in, you consent to receive transactional SMS messages
                    from QLearn by Medlearnity for account verification purposes only.
                    Msg &amp; data rates may apply. Text STOP to opt out. Text HELP
                    for help. Your mobile opt-in data will not be shared with third
                    parties.{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sms-disclosure-link"
                    >
                      Privacy Policy
                    </a>
                    {" | "}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sms-disclosure-link"
                    >
                      Terms &amp; Conditions
                    </a>
                  </Typography>
                </Box>
                {/* SDL consent surface — DARK: absent from the tree unless the
                    FE flag is on (spec AC 2); server stays authoritative. */}
                {sdlConsentAvailable() && <SdlStandingConsentSection />}
                {/* GL-7 Guided Learning self-consent surface — DARK: absent
                    unless the FE flag is on AND the server surface answers
                    (gl-consent spec D5); server stays authoritative. */}
                {glConsentAvailable() && <GlConsentSection />}
                {/* Account & sign-in connections — DARK: absent
                    unless REACT_APP_INSTITUTIONAL_LOGIN_ENABLED is on; the
                    server enforces recent auth + email proof independently.
                    Connections save independently of "Save Settings". */}
                {isInstitutionalLoginEnabled() && <AccountConnectionsSection />}
              </Box>

              <Button
                variant="contained"
                color="primary"
                disabled={!isSaveEnabled}
                onClick={handleSaveSettings}
                className="save-settings-btn"
              >
                Save Settings
              </Button>
            </Paper>
          )}
          {/* Session recording: the only place its choice lives (no notice or
              control in the study UI). Client-side only, so it works while (or
              if) preferences fail to load, and signed out too. Renders nothing
              unless recording is built in (never in the iPhone app). */}
          {/* layoutKey changes only while the preferences panel above loads
              (each part flips once), so #help-improve-qlearn lands on target
              and later edits never scroll the page. */}
          <RecordingSettingSection
            layoutKey={`${categoriesData?.loading}|${Boolean(categoryPreferences)}|${Boolean(userPreferenceData)}`}
          />
          {/* Reachable even while (or if) preferences fail to load. */}
          <Paper elevation={2} className="settings-paper-container settings-account-paper" component="section"
            aria-labelledby="settings-account-title">
            <Typography variant="h6" id="settings-account-title" gutterBottom>
              Account
            </Typography>
            <Box className="settings-account-row">
              <Typography variant="body2">
                Permanently delete your QLearn account and personal data.
              </Typography>
              <Button
                component="a"
                href={DELETE_ACCOUNT_PATH}
                onClick={(event) => {
                  // In-app navigation when a router is mounted (always, in the app); a plain
                  // link otherwise.
                  if (history) {
                    event.preventDefault();
                    history.push(DELETE_ACCOUNT_PATH);
                  }
                }}
                variant="outlined"
                className="settings-delete-account"
                data-testid="settings-delete-account"
              >
                Delete account
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}
